// This file dynamically loads the Learnosity Assess interface, as
// well as the scoring app from bubblesheet-core, but only when they
// are used by the loaded assessment.

import React, { useState, useEffect } from 'react';
import { useState$ } from 'rsm';
import { SavingModal } from './SavingModal';
import { SubmitErrorAlert } from './errorAlerts/SubmitErrorAlert';
import { FullscreenErrorAlert } from './errorAlerts/FullscreenErrorAlert';
import { SectionsDisplay } from './SectionsDisplay'

// IMPORTANT! Don't statically import all of bubblesheet-core/scoring,
// because it adds a lot of code and dependencies. All we need is the
// util file, which adds mere bytes to the main payload.
import { allLearnosityItems } from 'BubblesheetCore/scoring/util';



// These functions effectively mean the same thing:
export { allLearnosityItems };



let scoringAppPromise = null;

export const loadScoringApp = () => {
  if (!scoringAppPromise) {
    scoringAppPromise = import(/* webpackChunkName: "scoring" */ 'BubblesheetCore/scoring');
  }

  return scoringAppPromise;
};



export const prefetchScoringAppIfNeeded = testItems => {
  if (useLearnosityAssessForItems(testItems)) {
    loadScoringApp();
  }
};



const wrapperClass = ({ annotationsHidden }) => (
  `outer-assess-wrapper ${annotationsHidden ? 'annotations-hidden' : ''}`
);



export const LRNAssessWrapper = ({ rsm, propagateSpinnerSetter }) => {
  const { dispatch } = rsm
  const {
    renderRef,
    submitting,
    annotationsHidden,
    submitError,
    fullscreenError,
    parts,
    currentPartId,
    questionsCount,
    showSections,
  } = mapState(useState$(rsm))

  const currentSection = parts.findIndex(({ id }) => id === String(currentPartId))

  // Part of a hack to allow the rendering code to force the saving spinner to
  // show, even when the Scoring app doesn't have any ongoing activity:
  const [showSpinner, setShowSpinner] = useState(false)
  useEffect(() => {
    propagateSpinnerSetter?.(setShowSpinner)
  }, [setShowSpinner, propagateSpinnerSetter])

  useEffect(() => {
    const tagContent = () => {
      const el = document.querySelector('.lrn-assess-content');
      if (el && !el.id) {
        el.id = 'lrn-assessment-main';
        el.setAttribute('tabindex', '-1');
        return true;
      }
      return false;
    };

    if (tagContent()) return;

    const observer = new MutationObserver(() => {
      if (tagContent()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const groupFeatures = () => {
      const activeItem = document.querySelectorAll('[data-reference^="item"][style*="visibility: visible"]');

      if (activeItem.length < 1) {
        return;
      }

      activeItem[0].querySelectorAll('[data-lrn-widget-container^="feature"]').forEach(el => {
        if (el.parentNode?.hasAttribute('data-mc-feature-list')) return;

        const parent = el.parentNode;
        if (!parent) return;

        let wrapper = parent.querySelector(':scope > [data-mc-feature-list]');
        if (!wrapper) {
          wrapper = document.createElement('div');
          wrapper.setAttribute('role', 'list');
          wrapper.setAttribute('data-mc-feature-list', '');
          parent.insertBefore(wrapper, el);
        }

        wrapper.appendChild(el);
        if (!el.hasAttribute('role')) el.setAttribute('role', 'listitem');
      });
    };

    groupFeatures();
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        groupFeatures();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const t = window._t;
  const showSaving = submitting || showSpinner

  return (
    <div className={wrapperClass({ annotationsHidden })}>
      <a
        href="#lrn-assessment-main"
        className="lrn-skip-to-questions"
        onClick={(e) => {
          e.preventDefault();
          const items = Array.from(document.querySelectorAll('.lrn-assess-item'));
          const currentItem = items.find(el => getComputedStyle(el).visibility === 'visible');
          const target = currentItem?.querySelector('[tabindex="-1"]')
            || currentItem
            || document.getElementById('lrn-assessment-main');
          if (target) {
            if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
            target.focus();
          }
        }}
      >{t('Skip to questions')}</a>
      {showSections && (
        <SectionsDisplay
          sectionsCount={parts.length}
          currentSection={currentSection}
          questionsCount={questionsCount}
          onStart={() => dispatch({ type: 'START' })}
          onExit={() => dispatch({ type: 'EXIT' })}
        />
      )}
      {submitError && <SubmitErrorAlert key="sub-err" dispatch={dispatch} err={submitError} />}
      {fullscreenError && <FullscreenErrorAlert key="fsc-err" dispatch={dispatch} err={fullscreenError} />}
      <div key="learnosity-wrapper" ref={renderRef} />
      {showSaving && <SavingModal key="saving" />}
    </div>
  );
}

// Filter down props to just the relevant state:
export const mapState = ({ renderRef, submitting, annotationsHidden, submitError, fullscreenError, parts, testParams, testItems, partStarted, isMultiParts }) => ({
  renderRef,
  submitting,
  annotationsHidden: !!annotationsHidden,
  submitError,
  fullscreenError,
  parts,
  currentPartId: testParams.part_id,
  questionsCount: testItems.length,
  showSections: !partStarted && isMultiParts,
});

